/**
 * WCH Serial ISP Protocol for CH32V203
 * 
 * Protocol reference: https://github.com/ch32-rs/wchisp (serial transport)
 * 
 * SEND FORMAT:  [0x57, 0xAB] + [cmd] + [len_lo, len_hi] + [data...] + [checksum]
 *   checksum = sum of all bytes from cmd byte to last data byte (NOT including 0x57, 0xAB prefix)
 * 
 * RECV FORMAT:  [0x55, 0xAA] + [cmd] + [status] + [len_lo, len_hi] + [data...] + [checksum]
 *   status = 0x00 means success
 */

export const WCH_ISP_BAUD = 115200;

// Command codes
const CMD_IDENTIFY   = 0xA1;
const CMD_ISP_END    = 0xA2;
const CMD_ISP_KEY    = 0xA3;
const CMD_ERASE      = 0xA4;
const CMD_PROGRAM    = 0xA5;
const CMD_VERIFY     = 0xA6;
const CMD_READ_CFG   = 0xA7;
const CMD_WRITE_CONFIG = 0xA8;
const CMD_WRITE_CFG  = 0xA8;

// Build a full serial ISP command packet
// Format: 0x57 0xAB [cmd] [len_lo] [len_hi] [data...] [checksum]
// checksum = wrapping sum of [cmd, len_lo, len_hi, ...data]
function buildCmd(cmd: number, payload: number[]): Uint8Array {
  const len = payload.length;
  const raw = [cmd, len & 0xFF, (len >> 8) & 0xFF, ...payload];
  const cksum = raw.reduce((acc, b) => (acc + b) & 0xFF, 0);
  return new Uint8Array([0x57, 0xAB, ...raw, cksum]);
}

// Parse a received response. Returns null on failure.
// Expected: [0x55, 0xAA, cmd, status, len_lo, len_hi, ...data, checksum]
function parseResp(buf: Uint8Array, expectedCmd: number): { ok: boolean; data: Uint8Array } | null {
  if (buf.length < 6) return null;
  if (buf[0] !== 0x55 || buf[1] !== 0xAA) return null;
  if (buf[2] !== expectedCmd) return null;
  const status = buf[3];
  const len = buf[4] | (buf[5] << 8);
  if (buf.length < 6 + len + 1) return null;
  const data = buf.slice(6, 6 + len);
  // Verify checksum: sum of bytes from index 2 to 5+len
  const cksum = Array.from(buf.slice(2, 6 + len)).reduce((acc, b) => (acc + b) & 0xFF, 0);
  if (cksum !== buf[6 + len]) return null;
  return { ok: status === 0x00, data };
}

/**
 * Read the UID from chip via WCH Serial ISP protocol.
 * Optionally erase flash after reading UID.
 */
export async function wchIspReadUID(port: any, log: (s: string) => void, erase: boolean = false): Promise<string | null> {
  const toHex = (buf: Uint8Array) => Array.from(buf).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');

  let keepReading = true;
  let writer: any = null;
  let reader: any = null;
  let inBootloader = false;
  let uidString: string | null = null;

  try {
    await port.open({ baudRate: WCH_ISP_BAUD });

    // Auto-Boot sequence (Toggling RTS for CH340N / Custom BMCU board)
    await port.setSignals({ dataTerminalReady: false, requestToSend: true });
    await new Promise(r => setTimeout(r, 100));
    await port.setSignals({ dataTerminalReady: false, requestToSend: false });
    await new Promise(r => setTimeout(r, 300));

    writer = port.writable.getWriter();
    reader = port.readable.getReader();

    const rxBuffer: number[] = [];

    const readLoop = async () => {
      try {
        while (keepReading) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) rxBuffer.push(...Array.from(value as Uint8Array));
        }
      } catch { /* ignore */ }
    };
    readLoop();

    const waitForData = async (minLen: number, timeoutMs: number): Promise<Uint8Array<ArrayBuffer>> => {
      const start = Date.now();
      while (rxBuffer.length < minLen && (Date.now() - start) < timeoutMs) {
        await new Promise(r => setTimeout(r, 10));
      }
      return Uint8Array.from(rxBuffer.splice(0, rxBuffer.length));
    };

    // Send READ_CONFIG (0xA7) with mask 0x1F to read all config including UID
    // Payload: [0x1F, 0x00]
    const readCfgCmd = buildCmd(CMD_READ_CFG, [0x1F, 0x00]);
    let resp: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(0));

    for (let retry = 0; retry < 15; retry++) {
      await writer.write(readCfgCmd);
      resp = await waitForData(7, 150);
      if (resp.length >= 7 && resp[0] === 0x55 && resp[1] === 0xAA) break;
    }

    const parsed = parseResp(resp, CMD_READ_CFG);
    if (parsed && parsed.ok && parsed.data.length >= 8) {
      inBootloader = true;
      // UID is in last 8 bytes of config data
      const uid = parsed.data.slice(parsed.data.length - 8);
      uidString = Array.from(uid).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join('');
      log(`✅ Đọc UID thành công: ${uidString}`);
    } else {
      log('⚠️ Không đọc được UID. Resp: ' + toHex(resp));
    }

    if (inBootloader && erase) {
      log('Gửi lệnh xóa Flash...');
      const sectors = 128; // 128 sectors = 128KB for CH32V203
      const eraseCmd = buildCmd(CMD_ERASE, [
        sectors & 0xFF, (sectors >> 8) & 0xFF, (sectors >> 16) & 0xFF, (sectors >> 24) & 0xFF
      ]);
      await writer.write(eraseCmd);
      const eraseResp = await waitForData(7, 5000);
      const eraseParsed = parseResp(eraseResp, CMD_ERASE);
      if (eraseParsed && eraseParsed.ok) {
        log('✅ Xóa Flash thành công.');
      } else {
        log('❌ Xóa Flash thất bại! ' + toHex(eraseResp));
      }
    }

    keepReading = false;
    try { await reader.cancel(); } catch {}
    try { reader.releaseLock(); } catch {}
    try { writer.releaseLock(); } catch {}
    await port.close();

  } catch (e: any) {
    log(`Lỗi kết nối: ${e.message}`);
    keepReading = false;
    try { await reader.cancel(); } catch {}
    try { reader.releaseLock(); } catch {}
    try { writer.releaseLock(); } catch {}
    try { await port.close(); } catch { }
  }

  if (!inBootloader) {
    log('❌ Chip không phản hồi. Vui lòng giữ nút BOOT rồi cắm lại cáp USB!');
    return null;
  }

  return uidString;
}

/**
 * Flash firmware via WCH Serial ISP protocol.
 * Full flow: Identify → Read UID → ISP Key → Erase → Program chunks → ISP End
 */
export async function wchIspFlash(
  port: any,
  firmwareData: Uint8Array,
  log: (s: string) => void,
  onProgress: (pct: number) => void
): Promise<boolean> {
  const toHex = (buf: Uint8Array) => Array.from(buf).map(b => b.toString(16).padStart(2, '0').toUpperCase()).join(' ');
  let keepReading = true;
  let uid = new Uint8Array(8);
  let writer: any = null;
  let reader: any = null;
  let inBootloader = false;

  try {
    await port.open({ baudRate: WCH_ISP_BAUD });

    // Auto-Boot sequence
    await port.setSignals({ dataTerminalReady: false, requestToSend: true });
    await new Promise(r => setTimeout(r, 100));
    await port.setSignals({ dataTerminalReady: false, requestToSend: false });
    await new Promise(r => setTimeout(r, 300));

    writer = port.writable.getWriter();
    reader = port.readable.getReader();

    keepReading = true;
    const rxBuffer: number[] = [];
    const readLoop = async () => {
      try {
        while (keepReading) {
          const { value, done } = await reader.read();
          if (done) break;
          if (value) rxBuffer.push(...Array.from(value as Uint8Array));
        }
      } catch { }
    };
    readLoop();

    const waitForData = async (minLen: number, timeoutMs: number): Promise<Uint8Array<ArrayBuffer>> => {
      const start = Date.now();
      while (rxBuffer.length < minLen && (Date.now() - start) < timeoutMs) {
        await new Promise(r => setTimeout(r, 10));
      }
      return Uint8Array.from(rxBuffer.splice(0, rxBuffer.length));
    };

    let retrySession = true;
    let configBytesForProtect: Uint8Array | null = null;
    let verifyOk = true;

    while (retrySession) {
      retrySession = false;

      // Step 1: Identify chip
    // For CH32V203: device_id=0x19, device_type=0x29 (mcu_type=9, device_type=0x19, so device_type in identify = 0x19+0x10 = 0x29)
    // But wchisp sends device_id and device_type from yaml: device_type=0x19, chip_id in variants
    // Let's try 0x00 as wildcard first, then check response
    const identifyPayload = [
      0x00, 0x00,            // device_id, device_type (wildcard)
      ...Array.from(new TextEncoder().encode('MCU ISP & WCH.CN'))
    ];
    const identifyCmd = buildCmd(CMD_IDENTIFY, identifyPayload);

    let identResp: Uint8Array<ArrayBuffer> = new Uint8Array(new ArrayBuffer(0));
    for (let retry = 0; retry < 15; retry++) {
      await writer.write(identifyCmd);
      identResp = await waitForData(8, 150);
      if (identResp.length >= 8 && identResp[0] === 0x55 && identResp[1] === 0xAA) break;
    }

    const identParsed = parseResp(identResp, CMD_IDENTIFY);
    if (!identParsed || !identParsed.ok) {
      log('❌ Mất kết nối với thiết bị (Không phản hồi).');
      return false;
    }
    inBootloader = true;
    const chipId = identParsed.data.length > 0 ? identParsed.data[0] : 0x19;

    // Step 2: Read UID via READ_CONFIG (mask=0x10 for UID only)
    const readUidCmd = buildCmd(CMD_READ_CFG, [0x10, 0x00]);
    await writer.write(readUidCmd);
    const uidResp = await waitForData(19, 1000); // 6 header + 2 mask + 8 uid + 1 cksum = 18 bytes min
    const uidParsed = parseResp(uidResp, CMD_READ_CFG);
    if (uidParsed && uidParsed.ok && uidParsed.data.length >= 10) {
      uid = uidParsed.data.slice(2, 10);
    }

    // Step 2b: Read Config to check RRP (Write Protect)
    const readCfgCmd2 = buildCmd(CMD_READ_CFG, [0x07, 0x00]); // mask=0x07 (RDPR_USER_DATA_WPR)
    await writer.write(readCfgCmd2);
    const cfgResp = await waitForData(20, 1000); 
    const cfgParsed = parseResp(cfgResp, CMD_READ_CFG);
    if (cfgParsed && cfgParsed.ok && cfgParsed.data.length >= 14) {
      const configBytes = cfgParsed.data.slice(2, 14); // 12 bytes
      configBytesForProtect = new Uint8Array(configBytes);
      const rdpr = configBytes[0];
      
      if (rdpr !== 0xA5) {
        log('Đồng bộ cấu hình bo mạch...');
        const newConfig = new Uint8Array(configBytes);
        newConfig[0] = 0xA5; // unprotected
        newConfig[1] = 0x5A;
        newConfig[8] = 0xFF; // WPR0
        newConfig[9] = 0xFF; // WPR1
        newConfig[10] = 0xFF; // WPR2
        newConfig[11] = 0xFF; // WPR3
        
        const writeCfgPayload = [0x07, 0x00, ...Array.from(newConfig)];
        await writer.write(buildCmd(CMD_WRITE_CONFIG, writeCfgPayload));
        await waitForData(7, 1000);
        
        await writer.write(buildCmd(CMD_ISP_END, [0x01])); // 0x01 means config reset
        await waitForData(7, 500);
        
        // Wait for chip to reboot into bootloader
        await new Promise(r => setTimeout(r, 1000));
        rxBuffer.length = 0; // Clear buffer for new session
        retrySession = true;
        continue; // Loop back to IDENTIFY
      }
    }

    // Step 3: Send ISP Key (all-zero seed)
    // XOR key = [checksum_of_uid; 8], where key[7] += chip_id
    let uidChecksum = 0;
    for (let i = 0; i < 8; i++) uidChecksum = (uidChecksum + uid[i]) & 0xFF;
    const xorKey = new Uint8Array(8).fill(uidChecksum);
    xorKey[7] = (xorKey[7] + chipId) & 0xFF;

    const ispKeyPayload = new Array(30).fill(0); // all-zero seed
    const ispKeyCmd = buildCmd(CMD_ISP_KEY, ispKeyPayload);
    await writer.write(ispKeyCmd);
    const keyResp = await waitForData(7, 1000);
    const keyParsed = parseResp(keyResp, CMD_ISP_KEY);
    if (!keyParsed) {
      log('❌ Mất kết nối (Lỗi bảo mật).');
      return false;
    }

    // Step 4: Erase flash
    log('Chuẩn bị bộ nhớ thiết bị (Toàn bộ 64KB Flash)...');
    const sectors = 64; // Always erase all 64KB Flash (sectors 0..63, including NVM sector at 0x0800F000)
    const eraseCmd = buildCmd(CMD_ERASE, [
      sectors & 0xFF, (sectors >> 8) & 0xFF, (sectors >> 16) & 0xFF, (sectors >> 24) & 0xFF
    ]);
    await writer.write(eraseCmd);
    const eraseResp = await waitForData(7, 8000);
    const eraseParsed = parseResp(eraseResp, CMD_ERASE);
    if (!eraseParsed || !eraseParsed.ok) {
      log('❌ Quá trình chuẩn bị bộ nhớ thất bại!');
      return false;
    }

    // Step 5: Program in 56-byte chunks (CHUNK = 56 per wchisp)
    const CHUNK = 56;
    const totalChunks = Math.ceil(firmwareData.length / CHUNK);
    log('Đang nạp firmware vào thiết bị...');

    for (let i = 0; i < totalChunks; i++) {
      const offset = i * CHUNK;
      const chunk = firmwareData.slice(offset, offset + CHUNK);
      const addr = offset; // WCHISP actually uses 0-based relative offset

      // XOR encrypt data
      const xored = new Uint8Array(chunk.length);
      for (let j = 0; j < chunk.length; j++) {
        xored[j] = chunk[j] ^ xorKey[j % 8];
      }

      // Build program command payload:
      // [len_lo, len_hi] (of addr+padding+data) + [addr 4 bytes LE] + [padding 1 byte] + [xored data]
      const padding = 0x00; // padding byte (can be random, we use 0)
      const programPayload = [
        addr & 0xFF, (addr >> 8) & 0xFF, (addr >> 16) & 0xFF, (addr >> 24) & 0xFF,
        padding,
        ...Array.from(xored)
      ];
      const programCmd = buildCmd(CMD_PROGRAM, programPayload);

      await writer.write(programCmd);
      const writeResp = await waitForData(7, 3000);
      const writeParsed = parseResp(writeResp, CMD_PROGRAM);
      if (!writeParsed || !writeParsed.ok) {
        log(`❌ Lỗi nạp ở phân mảnh thứ ${i}!`);
        return false;
      }

      const pct = Math.round(((i + 1) / totalChunks) * 100);
      onProgress(pct);

      if (i === 0 || (i + 1) % 150 === 0 || i === totalChunks - 1) {
        log(`⚡ Đang nạp firmware: ${pct}%...`);
      }
    }
    
    // IMPORTANT: WCH chips require an empty write action to finalize the flash programming
    const finalAddr = totalChunks * CHUNK;
    const finalPayload = [
      finalAddr & 0xFF, (finalAddr >> 8) & 0xFF, (finalAddr >> 16) & 0xFF, (finalAddr >> 24) & 0xFF,
      0x00 // padding only, empty data
    ];
    await writer.write(buildCmd(CMD_PROGRAM, finalPayload));
    await waitForData(7, 1000);
    
    // Step 5b: Verify - read back and compare to confirm data is in flash
    log('Kiểm tra dữ liệu bộ nhớ Flash (Verify)...');
    verifyOk = true;
    let firstFailChunk = -1;
    for (let i = 0; i < Math.min(totalChunks, 5); i++) { // verify first 5 chunks as spot check
      const offset = i * CHUNK;
      const chunk = firmwareData.slice(offset, offset + CHUNK);
      const vaddr = offset;

      const xoredV = new Uint8Array(chunk.length);
      for (let j = 0; j < chunk.length; j++) {
        xoredV[j] = chunk[j] ^ xorKey[j % 8];
      }

      const verPayload = [
        vaddr & 0xFF, (vaddr >> 8) & 0xFF, (vaddr >> 16) & 0xFF, (vaddr >> 24) & 0xFF,
        0x00, // padding
        ...Array.from(xoredV)
      ];
      const verCmd = buildCmd(CMD_VERIFY, verPayload);
      await writer.write(verCmd);
      const verResp = await waitForData(7, 3000);
      const verParsed = parseResp(verResp, CMD_VERIFY);
      if (!verParsed || !verParsed.ok) {
        verifyOk = false;
        firstFailChunk = i;
        break;
      }
    }
    if (verifyOk) {
      log('✅ Dữ liệu bộ nhớ Flash hợp lệ 100%.');
    }

    // Step 6: Auto-Protect (Enable RRP)
    if (verifyOk && configBytesForProtect) {
      log('Hoàn tất cấu hình hệ thống...');
      const protectConfig = new Uint8Array(configBytesForProtect);
      protectConfig[0] = 0x00; // protected
      protectConfig[1] = 0xFF;
      const protectPayload = [0x07, 0x00, ...Array.from(protectConfig)];
      await writer.write(buildCmd(CMD_WRITE_CONFIG, protectPayload));
      await waitForData(7, 1000);
    }

    // Step 7: End ISP session (chip internally reboots into user code after ISP_END)
    const endCmd = buildCmd(CMD_ISP_END, [0x00]); // reason=0 (normal reboot)
    await writer.write(endCmd);
    // ISP_END causes chip to reboot. After response is received, chip reboots.
    // Just wait briefly for the response and let chip reboot naturally.
    await waitForData(7, 500);
    await new Promise(r => setTimeout(r, 300)); // Give chip time to reboot into user code
    
    log('🎉 Nạp hoàn tất! Thiết bị đang khởi động lại...');

    } // end of while(retrySession) loop

  } catch (e: any) {
    log(`Lỗi: ${e.message}`);
  } finally {
    keepReading = false;
    try { await reader?.cancel(); } catch {}
    try { reader?.releaseLock(); } catch {}
    try { writer?.releaseLock(); } catch {}
    try { await port.close(); } catch {}
  }

  if (!inBootloader) {
    log('❌ Không kết nối được. Giữ BOOT, cắm lại cáp và thử lại!');
    return false;
  }

  return true;
}
